import { sqlQuery } from '@/shared/helpers/api.ts';
import { http } from '@/shared/httpWrapper';
import {
    CodeRefChangedHandlers,
    CodeRefFilterFunctions,
    CodeRefSelectorCode,
    CodeRefValidationResult,
    CodeRefValidators
} from '@/shared/codeedit/referableCodeHandling.ts';
import { CodeEditFields } from '@/shared/codeedit/codeEditFields.ts';

export interface ProjectMasterCode extends CodeEditFields {
    Country: string | null;
    Phase: string | null;
    ProfitCenter: string | null;
    PlanProject: string | null;
}

export type PlanProjectReferenceCount = {
    Identity: string;
    UsageCount: number;
    Country: string;
    Phase: string;
    ProfitCenter: string;
};

// no object will have this structure, but it allows using `keyof` as the type of object key on both PlanProjectReferenceCount and ProjectMasterCode
export type PlanProjectProjectMasterIntersection = PlanProjectReferenceCount | ProjectMasterCode;

export async function loadPlanProjectReferenceCount(): Promise<PlanProjectReferenceCount[]> {
    const query = `SELECT pp.[Identity], count(pm.Id) UsageCount, ph.[Identity] as Phase, pp.ProfitCenter, pp.Country
                   FROM PlanProject pp
                       LEFT JOIN ProjectMaster pm ON pm.PlanProject_Id = pp.Id
                       LEFT JOIN ProjectPhase ph ON pp.DGLatest = ph.MilestoneStart
                   GROUP BY pp.[Identity], pp.ProfitCenter, pp.Country, ph.[Identity]`;

    const response = await sqlQuery(http, { query: query });
    return response.data as PlanProjectReferenceCount[];
}

export function attributeRefCodeFilter(ppUsageCounts: PlanProjectReferenceCount[] | null, currentPlanProjectIdentity?: string): CodeRefFilterFunctions<ProjectMasterCode> {
    const planProjectUsageCounts = ppUsageCounts
        ? new Map(ppUsageCounts.map((pp) => [pp.Identity, pp.UsageCount]))
        : null;
    return {
        PlanProject: (planProject: CodeRefSelectorCode) => {
            if (!planProjectUsageCounts) {
                return true;
            }

            const isCurrentPlanProject = currentPlanProjectIdentity && planProject.identity === currentPlanProjectIdentity;
            const isUsedByProjectMaster = (planProjectUsageCounts.get(planProject.identity) ?? 0) > 0;
            const isSelectable = isCurrentPlanProject || !isUsedByProjectMaster;

            planProject.isSelectable = isSelectable;
            planProject.unselectableReason = isSelectable ? undefined : 'Already assigned to another ProjectMaster';

            return true;
        }
    };
}

export function attributeRefCodeValidator(ppUsageCounts: PlanProjectReferenceCount[] | null): CodeRefValidators<ProjectMasterCode> {
    return {
        PlanProject: (modifiedCode: CodeEditFields, originalCode: CodeEditFields) => {
            const result: CodeRefValidationResult = {
                message: '',
                messageType: '',
                error: false
            };

            if (!modifiedCode.PlanProject) {
                return result;
            }

            if (!ppUsageCounts) {
                result.message = 'Validating..';
                result.messageType = 'is-info';
                return result;
            }

            const planProjectUsageCount = ppUsageCounts
                .find((pp) => pp.Identity === modifiedCode.PlanProject)?.UsageCount ?? 0;

            // If plan project is modified, we need to check if the new plan project is in use by other ProjectMasters
            const isModified = modifiedCode.PlanProject !== originalCode.PlanProject;

            const isDuplicate = planProjectUsageCount > (isModified ? 0 : 1);

            if (isDuplicate) {
                result.message = 'Plan project is already used by another ProjectMaster';
                result.messageType = isModified ? 'is-danger' : 'is-warning';
                result.error = isModified;
                return result;
            } else {
                return result;
            }
        }
    };
}

export function attributeRefCodeChangedHandlers(planProjects: PlanProjectReferenceCount[]): CodeRefChangedHandlers<ProjectMasterCode> {
    return {
        PlanProject: (local: CodeEditFields) => {
            const pp = planProjects.find((x) => x.Identity === local.PlanProject);
            if (!pp) {
                return;
            }

            local.Country = pp.Country ?? '';
            local.Phase = pp.Phase ?? '';
            local.ProfitCenter = pp.ProfitCenter ?? '';
        }
    };
}

export function PlanProjectProjectMasterAttributeDelta(
    keys: (keyof PlanProjectProjectMasterIntersection)[],
    projectMaster: ProjectMasterCode,
    planProject: PlanProjectReferenceCount,
    includeEmpty: boolean = false
): (keyof PlanProjectProjectMasterIntersection)[] {
    return keys.filter((key) => {
        if (!planProject[key] && !includeEmpty) {
            return false;
        }
        return planProject[key] !== projectMaster[key];
    });
}
